import React, { useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function SMNAdmin() {
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Admin - In Smart Media Inquiry Management</title>
      </Helmet>
      <SMNAdminContent />
    </>
  );
}

function SMNAdminContent() {
  const [adminUsername, setAdminUsername] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [inquiries, setInquiries] = useState([]);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  const fetchInquiries = async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = window.btoa(`${adminUsername}:${adminPassword}`);
      const res = await fetch(`${BACKEND_URL}/api/admin/inquiries`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${auth}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to fetch");
      setInquiries(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setAdminUsername("");
      setAdminPassword("");
    }
  };

  const filtered = useMemo(() => {
    if (!query) return inquiries;
    const q = query.toLowerCase();
    return inquiries.filter(
      (i) =>
        (i.name || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.phone || "").toLowerCase().includes(q) ||
        (i.message || "").toLowerCase().includes(q),
    );
  }, [inquiries, query]);

  const exportCSV = () => {
    if (!inquiries.length) return;
    const header = [
      "id",
      "name",
      "email",
      "phone",
      "businessType",
      "serviceInterest",
      "budgetRange",
      "message",
      "createdAt",
    ];
    const rows = inquiries.map((r) =>
      header
        .map((h) => `"${(r[h] || "").toString().replace(/"/g, '""')}"`)
        .join(","),
    );
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `inquiries_${new Date().toISOString()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white py-12">
      <div className="max-w-6xl mx-auto px-6">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-8 mb-6">
            <img
              src="/logoTransparant.png"
              alt="logo"
              className="h-16 w-auto"
            />
            <h1 className="text-3xl font-bold mb-4 ">
              ISM Admin — Inquiry Logs
            </h1>
          </div>

          <p className="text-sm text-muted-foreground mb-6">
            Hidden admin panel. Provide your admin username and password to
            fetch inquiry logs.
          </p>

          <div className="grid md:grid-cols-3 gap-3 mb-6">
            <div>
              <Label>Admin Username</Label>
              <Input
                value={adminUsername}
                onChange={(e) => setAdminUsername(e.target.value)}
                placeholder="Username"
              />
            </div>
            <div>
              <Label>Admin Password</Label>
              <Input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                placeholder="Password"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button
                onClick={fetchInquiries}
                disabled={loading || !adminUsername || !adminPassword}
              >
                Fetch
              </Button>
              <Button
                onClick={() => {
                  setInquiries([]);
                  setError(null);
                }}
                variant="ghost"
              >
                Clear
              </Button>
              <Button
                onClick={exportCSV}
                disabled={!inquiries.length}
                variant="secondary"
              >
                Export CSV
              </Button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <Input
              placeholder="Search name, email, phone or message"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-[80%]"
            />
            <div className="text-sm text-muted-foreground">
              Results: {filtered.length}
            </div>
          </div>

          {error && <div className="mb-4 text-red-600">{error}</div>}

          <div className="overflow-auto">
            <table className="min-w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Phone
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Business
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Service
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Budget
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Message
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                    Received
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((row) => (
                  <tr
                    key={row.id || row.email + row.createdAt}
                    className="hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {row.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-600 break-words">
                      {row.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {row.phone}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.businessType || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.serviceInterest || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {row.budgetRange || "-"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xl truncate">
                      {row.message}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && !loading && (
              <div className="p-6 text-center text-sm text-gray-500">
                No inquiries found.
              </div>
            )}
            {loading && (
              <div className="p-6 text-center text-sm text-gray-500">
                Loading inquiries...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
